import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import '../styles/ServiceDetailPage.css';

// Real-time remaining countdown calculation
const computeRemainingTime = (deadlineVal, receptionEndVal) => {
  let targetTime = 0;
  if (deadlineVal) {
    const d = new Date(deadlineVal);
    if (!isNaN(d.getTime())) {
      targetTime = d.getTime();
    }
  }

  // If no deadline, parse from receptionEnd (e.g., '25.10.2026')
  if (!targetTime && receptionEndVal) {
    const parts = String(receptionEndVal).split('.');
    if (parts.length === 3) {
      const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10), 23, 59, 59);
      if (!isNaN(d.getTime())) targetTime = d.getTime();
    }
  }

  // Default fallback target: 2026-10-25 23:59:59
  if (!targetTime) {
    targetTime = new Date(2026, 9, 25, 23, 59, 59).getTime();
  }

  const now = Date.now();
  const difference = targetTime - now;

  if (difference <= 0) {
    return { days: '00', hours: '00', minutes: '00', seconds: '00', expired: true };
  }

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0'),
    expired: false
  };
};

export default function ServiceDetailPage({ user }) {
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const serviceId = 'phd-exam';

  const [serviceDetails, setServiceDetails] = useState({
    title: 'Chet tilidan va mutaxassislik fanlaridan malakaviy imtihon.',
    deadline: '2026-10-25T23:59:59.000Z',
    receptionStart: '05.10.2026',
    receptionEnd: '25.10.2026',
    receptionPeriod: '2026-yil 5-oktabrdan 25-oktabrgacha',
    workingHours: '09:00 – 17:00',
    contactPhone: '+998 (75) 220-00-25',
    contactPhone2: '+998 (88) 214-88-80',
    processingDays: '30 kun',
    timeToApply: 9,
    status: 'active'
  });

  const [timeLeft, setTimeLeft] = useState(() => computeRemainingTime('2026-10-25T23:59:59.000Z', '25.10.2026'));

  const [reviewsData, setReviewsData] = useState({
    avgRating: 0.0,
    totalReviews: 0,
    totalApplications: 0,
    reviews: []
  });

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showAdminNoticeModal, setShowAdminNoticeModal] = useState(false);
  const [editingAdminReview, setEditingAdminReview] = useState(null);
  const [editAdminForm, setEditAdminForm] = useState({ userName: '', rating: 5, comment: '' });
  const [newRating, setNewRating] = useState(5);
  const [newComment, setNewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewMessage, setReviewMessage] = useState('');

  // Fetch service settings and reviews from server
  useEffect(() => {
    const fetchServiceAndReviews = async () => {
      try {
        const [revRes, svcRes] = await Promise.all([
          axios.get(`/api/services/${serviceId}/reviews`).catch(() => null),
          axios.get(`/api/services/${serviceId}`).catch(() => null)
        ]);

        if (revRes && revRes.data) {
          setReviewsData({
            avgRating: revRes.data.avgRating !== undefined && revRes.data.avgRating !== null ? revRes.data.avgRating : 0.0,
            totalReviews: revRes.data.totalReviews || 0,
            totalApplications: revRes.data.totalApplications || 0,
            reviews: revRes.data.reviews || []
          });
        }

        if (svcRes && svcRes.data && svcRes.data.service) {
          setServiceDetails(prev => ({
            ...prev,
            ...svcRes.data.service
          }));
        }
      } catch (err) {
        console.error('Service and reviews fetching error:', err);
      }
    };

    fetchServiceAndReviews();

    const handleFocus = () => {
      fetchServiceAndReviews();
    };
    window.addEventListener('focus', handleFocus);
    const syncInterval = setInterval(fetchServiceAndReviews, 30000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(syncInterval);
    };
  }, [serviceId]);

  // Live countdown timer calculating remaining time to service deadline
  useEffect(() => {
    const tick = () => {
      setTimeLeft(computeRemainingTime(serviceDetails?.deadline, serviceDetails?.receptionEnd));
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [serviceDetails?.deadline, serviceDetails?.receptionEnd]);

  const handleUseService = () => {
    if (timeLeft.expired && user?.role !== 'admin') {
      return;
    }
    if (!user) {
      navigate('/login', {
        state: {
          infoMessage: t('Malakaviy imtihon arizasini topshirish uchun avval tizimga kiring.'),
          returnUrl: '/register'
        }
      });
    } else if (user.role === 'admin') {
      // Administrator koʻrish rejimida - ariza topshirish faqat talabgorlarga tegishli
      setShowAdminNoticeModal(true);
    } else {
      navigate('/register');
    }
  };

  const handleAdminLogoutAndApply = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login', {
      state: {
        infoMessage: t('Talabgor sifatida tizimga kiring yoki roʻyxatdan oʻting.'),
        returnUrl: '/register'
      }
    });
  };

  const handleOpenReviewModal = () => {
    if (!user) {
      navigate('/login', {
        state: {
          infoMessage: t('Xizmatga baho va sharh qoldirish uchun avval tizimga kiring.'),
          returnUrl: '/services/phd-exam'
        }
      });
      return;
    }
    if (user.role === 'admin') {
      const adminAlert = lang === 'ru'
        ? '🛡️ Администратор не может оставлять оценку или отзыв на услуги. Оценки и отзывы могут оставлять только соискатели (исследователи), воспользовавшиеся услугой.'
        : lang === 'en'
        ? '🛡️ Administrator cannot leave ratings or reviews on services. Ratings and reviews are submitted only by applicants (researchers) who used the service.'
        : '🛡️ Administrator xizmatlarga õzi baho yoki şarh qoldira olmaydi. Baho va şarhlar faqat xizmatdan foydalangan talabgorlar (tadqiqotçilar) tomonidan beriladi.';
      alert(adminAlert);
      return;
    }
    setShowReviewModal(true);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }
    if (!newComment.trim()) return;

    try {
      setSubmittingReview(true);
      const res = await axios.post(`/api/services/${serviceId}/reviews`, {
        rating: newRating,
        comment: newComment,
        userName: user?.name || user?.email || 'Talabgor'
      });

      if (res.data && res.data.success) {
        setReviewMessage(t('Bahoyingiz va sharhingiz muvaffaqiyatli qabul qilindi!'));
        setReviewsData((prev) => ({
          avgRating: res.data.avgRating || prev.avgRating,
          totalReviews: res.data.totalReviews || prev.totalReviews + 1,
          totalApplications: res.data.totalApplications || prev.totalApplications,
          reviews: [res.data.review, ...prev.reviews]
        }));
        setNewComment('');
        setTimeout(() => {
          setShowReviewModal(false);
          setReviewMessage('');
        }, 1500);
      }
    } catch (err) {
      console.error('Review submit error:', err);
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleAdminEditReview = (rev) => {
    setEditingAdminReview(rev);
    setEditAdminForm({
      userName: rev.userName || '',
      rating: rev.rating || 5,
      comment: rev.comment || ''
    });
  };

  const handleAdminSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingAdminReview) return;
    try {
      const res = await axios.put(`/api/admin/services/${serviceId}/reviews/${editingAdminReview.id}`, {
        userName: editAdminForm.userName,
        rating: Number(editAdminForm.rating),
        comment: editAdminForm.comment
      });
      if (res.data) {
        setReviewsData(prev => ({
          ...prev,
          avgRating: res.data.avgRating,
          totalReviews: res.data.totalReviews,
          reviews: res.data.reviews
        }));
        setServiceDetails(prev => ({
          ...prev,
          rating: res.data.avgRating
        }));
      }
      setEditingAdminReview(null);
    } catch (err) {
      alert('Sharhni saqlashda xatolik: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAdminDeleteReview = async (reviewId) => {
    const confirmMsg = lang === 'ru'
      ? 'Вы действительно хотите удалить этот отзыв? В результате средняя оценка услуги будет пересчитана.'
      : lang === 'en'
      ? 'Are you sure you want to delete this review? The average rating will be recalculated.'
      : 'Haqiqatan ham ushbu şarhni õçirib taşlamoqçimisiz? Natijada xizmatning õrtaça bahosi qayta hisoblanadi.';
    if (!window.confirm(confirmMsg)) {
      return;
    }
    try {
      const res = await axios.delete(`/api/admin/services/${serviceId}/reviews/${reviewId}`);
      if (res.data) {
        setReviewsData(prev => ({
          ...prev,
          avgRating: res.data.avgRating,
          totalReviews: res.data.totalReviews,
          reviews: res.data.reviews
        }));
        setServiceDetails(prev => ({
          ...prev,
          rating: res.data.avgRating
        }));
      }
    } catch (err) {
      alert('Oʻchirishda xatolik: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="service-detail-page">
      <div className="container">
        {/* Breadcrumb Navigation matching Image */}
        <div className="detail-breadcrumb">
          <span className="crumb-link" onClick={() => navigate(user?.role === 'admin' ? '/admin' : '/')}>
            {user?.role === 'admin' ? t('Admin paneli') : t('Bosh sahifa')}
          </span>
          <span className="crumb-sep">/</span>
          <span className="crumb-link" onClick={() => navigate('/portal')}>{t('Interaktiv elektron xizmatlar', 'Xizmatlar')}</span>
          <span className="crumb-sep">/</span>
          <span className="crumb-text">{t('Chet tilidan va...')}</span>
        </div>

        {/* Administrator Preview Mode Ribbon */}
        {user && user.role === 'admin' && (
          <div className="admin-service-preview-strip">
            <div className="preview-strip-left">
              <span className="preview-pulse-dot"></span>
              <span className="preview-strip-text">
                <strong>{t('Administrator Koʻrish Rejimi:')}</strong> {t('Siz xizmatning talabgorlarga koʻrinish holatini tekshirmoqdasiz. Qilingan barcha oʻzgarishlar real vaqtda shu yerda aks etadi. Xizmatdan foydalanish (ariza topshirish) esa faqat talabgorlarga tegishli.')}
              </span>
            </div>
            <button className="preview-strip-btn" onClick={() => navigate('/admin')}>
              ⚙️ {t('Admin boshqaruv paneliga oʻtish', 'Admin boshqaruv paneliga qaytish')}
            </button>
          </div>
        )}

        {/* Top Header Row matching Image */}
        <div className="service-header-row">
          <div className="service-header-left">
            <div className="service-green-icon-box">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                <path d="M2 12h20" />
                <path d="M12 18v4" />
              </svg>
            </div>
            <div className="service-title-block">
              <h1 className="service-detail-title">
                {t(serviceDetails.title || 'Chet tilidan va mutaxassislik fanlaridan malakaviy imtihon.')}
              </h1>
              <div className="service-submeta-row">
                <span className="submeta-item">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                  <span>{t(serviceDetails.processingDays || '30 kun')}</span>
                </span>
                <span className="submeta-item submeta-date start-date" title={t('Hujjat qabulining boshlanish sanasi')}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  <span>{serviceDetails.receptionStart || '05.10.2026'}</span>
                </span>
                <span className="submeta-item submeta-date end-date" title={t('Hujjat qabulining oxirgi tugash muddati')}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  <span>{serviceDetails.receptionEnd || '25.10.2026'}</span>
                </span>
              </div>
            </div>
          </div>

          <button
            className={`btn-use-action-blue ${user?.role === 'admin' ? 'admin-preview-btn' : ''} ${timeLeft.expired && user?.role !== 'admin' ? 'is-disabled-expired' : ''}`}
            onClick={handleUseService}
            disabled={timeLeft.expired && user?.role !== 'admin'}
            title={
              user?.role === 'admin'
                ? t('Administrator koʻrish rejimi')
                : timeLeft.expired
                ? t('Ushbu xizmat boʻyicha arizalar qabul qilish muddati yakunlangan')
                : t('Xizmatdan foydalanish')
            }
          >
            {timeLeft.expired && user?.role !== 'admin' ? (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            )}
            <span>
              {user?.role === 'admin'
                ? t('Xizmatdan foydalanish (Koʻrish)')
                : timeLeft.expired
                ? t('Qabul muddati yakunlangan')
                : t('Xizmatdan foydalanish')}
            </span>
          </button>
        </div>

        {/* Countdown Timer to Service Deadline */}
        <div className="service-deadline-timer-container">
          <h4 className="deadline-banner-header">{t('Hujjat topshirishning oxirgi muddatiga qoldi:')}</h4>
          {timeLeft.expired ? (
            <div className="deadline-expired-pill">
              <span>⚠️ {t('Ushbu xizmat boʻyicha arizalar qabul qilish muddati yakunlangan')}</span>
            </div>
          ) : (
            <div className="deadline-timer-cards-row">
              <div className="deadline-timer-box">
                <span className="timer-box-digits">{timeLeft.days}</span>
                <span className="timer-box-unit">{t('kun')}</span>
              </div>
              <div className="deadline-timer-box">
                <span className="timer-box-digits">{timeLeft.hours}</span>
                <span className="timer-box-unit">{t('soat')}</span>
              </div>
              <div className="deadline-timer-box">
                <span className="timer-box-digits">{timeLeft.minutes}</span>
                <span className="timer-box-unit">{t('daqiqa')}</span>
              </div>
              <div className="deadline-timer-box">
                <span className="timer-box-digits">{timeLeft.seconds}</span>
                <span className="timer-box-unit">{t('soniya')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Main Two-Column Layout strictly matching Image */}
        <div className="detail-two-column-layout">
          {/* Left Column: Stats Banner + Connected Timeline Cards */}
          <div className="detail-left-col">
            {/* 3 Metrics Top Banner */}
            <div className="detail-stats-card-blue" onClick={handleOpenReviewModal} style={{ cursor: 'pointer' }} title={t('+ Baho va sharh qoldirish')}>
              <div className="stat-col rating-col">
                <span className="stat-col-val rating-val" style={{ color: reviewsData.totalReviews > 0 ? '#ca8a04' : '#94a3b8' }}>
                  {reviewsData.totalReviews > 0 ? Number(reviewsData.avgRating).toFixed(1) : '0.0'}
                </span>
                <div className="stat-col-stars" style={{ color: reviewsData.totalReviews > 0 ? '#f59e0b' : '#cbd5e1' }}>
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
              <div className="stat-col-divider"></div>
              <div className="stat-col time-col">
                <span className="stat-col-val time-val">{serviceDetails.timeToApply || 9} {t('daqiqa')}</span>
                <span className="stat-col-sub">{t('ariza qoldirish uchun yetarli')}</span>
              </div>
              <div className="stat-col-divider"></div>
              <div className="stat-col usage-col">
                <span className="stat-col-val usage-val">{reviewsData.totalApplications}</span>
                <span className="stat-col-sub">{t('marotaba foydalanilgan')}</span>
              </div>
            </div>

            {/* Connected Vertical Timeline with 5 Cards */}
            <div className="detail-timeline-container">
              {/* Item 1: Boʻlim nomi */}
              <div className="detail-timeline-item">
                <div className="timeline-badge-icon">
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2">
                    <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v4M12 14v4M16 14v4" />
                  </svg>
                </div>
                <div className="timeline-card-box">
                  <div className="timeline-card-label">{t('Boʻlim nomi')}</div>
                  <div className="timeline-card-value">{t('Ilmiy tadqiqotlar va doktorantura boʻlimi')}</div>
                </div>
              </div>

              {/* Item 2: Aloqa maʼlumotlari */}
              <div className="detail-timeline-item">
                <div className="timeline-badge-icon">
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div className="timeline-card-box">
                  <div className="timeline-card-label">{t('Aloqa maʼlumotlari')}</div>
                  <div className="timeline-card-value">{serviceDetails.contactPhone || '+998 (75) 220-00-25'}</div>
                </div>
              </div>

              {/* Item 3: Kim foydalanishi mumkin */}
              <div className="detail-timeline-item">
                <div className="timeline-badge-icon">
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div className="timeline-card-box">
                  <div className="timeline-card-label">{t('Kim foydalanishi mumkin')}</div>
                  <div className="timeline-tags-row">
                    <span className="timeline-tag-pill">{t('Mehmon')}</span>
                    <span className="timeline-tag-pill">{t('Talaba')}</span>
                    <span className="timeline-tag-pill">{t('Xodim')}</span>
                  </div>
                </div>
              </div>

              {/* Item 4: Xizmat koʻrsatish muddati */}
              <div className="detail-timeline-item">
                <div className="timeline-badge-icon">
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2">
                    <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
                  </svg>
                </div>
                <div className="timeline-card-box">
                  <div className="timeline-card-label">{t('Xizmat koʻrsatish muddati')}</div>
                  <div className="timeline-card-value">{t(serviceDetails.processingDays || '30 kun')}</div>
                </div>
              </div>

              {/* Item 5: Huquqiy asos */}
              <div className="detail-timeline-item">
                <div className="timeline-badge-icon">
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2">
                    <path d="M14 13l-7.5 7.5a2.121 2.121 0 0 1-3-3L11 10m3 3l5-5m-5 5l2 2m3-7l2 2a1.5 1.5 0 0 0 2.12 0l1.42-1.42a1.5 1.5 0 0 0 0-2.12l-3.54-3.54a1.5 1.5 0 0 0-2.12 0L14 4.5a1.5 1.5 0 0 0 0 2.12l2 2" />
                  </svg>
                </div>
                <div className="timeline-card-box">
                  <div className="timeline-card-label">{t('Huquqiy asos')}</div>
                  <div className="timeline-card-value" style={{ lineHeight: '1.55' }}>
                    {lang === 'ru'
                      ? 'Данный порядок утвержден постановлением Президиума Высшей аттестационной комиссии при Кабинете Министров Республики Узбекистан от 17 февраля 2016 года (№ 2764). Действующая редакция: 19 августа 2024 года.'
                      : lang === 'en'
                      ? 'This procedure was approved by the Presidium of the Supreme Attestation Commission under the Cabinet of Ministers of the Republic of Uzbekistan on February 17, 2016 (No. 2764). Current revision: August 19, 2024.'
                      : 'Uşbu tartib Õzbekiston Respublikasi Vazirlar Mahkamasi huzuridagi Oliy attestatsiya komissiyasi Rayosatining 2016-yil 17-fevraldagi qarori (№ 2764) bilan tasdiqlangan. Amaldagi sõngi tahrir: 2024-yil 19-avgust.'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: "Haqida" details matching Image exactly */}
          <div className="detail-right-col">
            <div className="detail-about-card">
              <h3 className="about-card-title">{t('Haqida')}</h3>
              <p className="about-card-desc">
                {lang === 'ru'
                  ? 'В ноябре текущего года запланировано проведение квалификационных экзаменов по иностранному языку (английский язык) и специальным предметам для научных исследователей.'
                  : lang === 'en'
                  ? 'In November of this year, qualifying examinations in foreign languages (English) and specialty subjects are scheduled for scientific researchers.'
                  : 'Joriy yilning noyabr oyida ilmiy tadqiqotçilar uçun çet tili (ingliz tili) va mutaxassislik fanlari bõyiça malakaviy imtihonlar õtkazilişi rejalaştirilgan.'}
              </p>

              <div className="about-info-section">
                <div className="about-section-heading">
                  <span className="sec-emoji">🗓️</span>
                  <span>
                    <strong>{t('Hujjatlar qabul qilish muddati:')}</strong>{' '}
                    {lang === 'ru'
                      ? 'С 5 по 25 октября 2026 года.'
                      : lang === 'en'
                      ? 'From October 5 to October 25, 2026.'
                      : '2026-yil 5-oktabrdan 25-oktabrgacha.'}
                  </span>
                </div>
              </div>

              <div className="about-info-section">
                <div className="about-section-heading">
                  <span className="sec-emoji">📝</span>
                  <span><strong>{t('Imtihon tartibi:')}</strong></span>
                </div>
                <ul className="about-bullet-list">
                  {lang === 'ru' ? (
                    <>
                      <li>Соискатели проходят онлайн-регистрацию через портал интерактивных услуг Университета Турон;</li>
                      <li>Дата и место проведения экзамена определяются ответственной комиссией университета в установленном порядке;</li>
                      <li>Информация о дате и времени проведения экзамена публикуется через официальный Telegram-канал.</li>
                    </>
                  ) : lang === 'en' ? (
                    <>
                      <li>Applicants register online through the Turon University interactive services portal;</li>
                      <li>The examination date and venue are determined by the university examination committee;</li>
                      <li>Exam schedule and updates are officially announced via the university Telegram channel.</li>
                    </>
                  ) : (
                    <>
                      <li>Talabgorlar Turon universiteti interaktiv xizmatlar portali orqali onlayn rõyxatdan õtadilar;</li>
                      <li>Imtihon sanasi va joyi universitet masʼul komissiyasi tomonidan belgilangan tartibda aniqlanadi;</li>
                      <li>Imtihon kuni va vaqti haqidagi maʼlumotlar rasmiy Telegram kanali orqali eʼlon qilinadi.</li>
                    </>
                  )}
                </ul>
              </div>

              <div className="about-info-section">
                <div className="about-section-heading">
                  <span className="sec-emoji">👥</span>
                  <span><strong>{t('Imtihonga oid yangiliklar uchun Telegram kanal:')}</strong> <a href="https://t.me/turonuniversity" target="_blank" rel="noopener noreferrer" className="telegram-blue-link">https://t.me/turonuniversity</a></span>
                </div>
              </div>

              <div className="about-info-section">
                <div className="about-section-heading">
                  <span className="sec-emoji">💰</span>
                  <span>
                    <strong>{t('Xizmat narxi:')}</strong>{' '}
                    {lang === 'ru'
                      ? 'В соответствии с решением Совета Университета Турон, для соискателей из других высших образовательных учреждений, научно-исследовательских институтов и организаций сбор за сдачу квалификационного экзамена (включая дополнительные экзамены по требованиям ВАК) установлен в размере 1 базовой расчетной величины (1 БРВ). Для докторантов и исследователей Университета Турон услуга бесплатна.'
                      : lang === 'en'
                      ? 'In accordance with the resolution of the Turon University Council, the examination fee for applicants from external higher education and research institutions is fixed at 1 base calculation amount (1 BRV). The service is free of charge for Turon University doctoral candidates and researchers.'
                      : 'Turon universiteti Kengaşining tegişli qaroriga muvofiq, boşqa oliy taʼlim muassasalari, ilmiy-tadqiqot institutlari va taşkilotlardan ariza topşirgan talabgorlar uçun malakaviy imtihon (şu jumladan OAK tomonidan belgilangan qõşimça imtihonlar) topşiriş tõlovi bazaviy hisoblaş miqdorining 1 baravari (BHMning 1 baravari) etib belgilangan. Turon universiteti doktorant va tadqiqotçilari uçun xizmat bepul.'
                    }
                  </span>
                </div>
              </div>

              <div className="about-info-section">
                <div className="about-section-heading">
                  <span className="sec-emoji">🏦</span>
                  <span><strong>{t('Toʻlov uchun rekvizitlar:')}</strong></span>
                </div>
                <ul className="about-bullet-list rekvizit-list">
                  <li><strong>{t('Tashkilot:')}</strong> {lang === 'ru' ? 'Университет Турон' : lang === 'en' ? 'Turon University' : 'Turon universiteti'}</li>
                  <li><strong>{t('Manzil:')}</strong> {lang === 'ru' ? 'г. Карши, ул. Насаф, 1/14' : lang === 'en' ? 'Karshi city, Nasaf str., 1/14' : 'Qarşi ş., Nasaf kõçasi, 1/14-uy'}</li>
                  <li><strong>{t('ShXRa:')}</strong> 400110860104017094100350001</li>
                  <li><strong>{t('STIR / INN:')}</strong> 309852147</li>
                  <li><strong>{t('Gʻazna:')}</strong> {t('Moliya vazirligi Gʻaznachiligi')}</li>
                  <li><strong>{t('Hisob raqam:')}</strong> 23402000300100001010</li>
                  <li><strong>{t('MFO:')}</strong> 00014</li>
                </ul>
              </div>

              <div className="about-info-section">
                <div className="about-section-heading">
                  <span className="sec-emoji">📞</span>
                  <span><strong>{t('Murojaat uchun aloqa:')}</strong></span>
                </div>
                <ul className="about-bullet-list">
                  <li><strong>{t('Telefon:')}</strong> {serviceDetails.contactPhone || '+998 (75) 220-00-25'}</li>
                  <li><strong>{t('Elektron pochta:')}</strong> {serviceDetails.contactEmail || 'turonuniversity@gmail.com'}</li>
                  <li><strong>{t('Ish vaqti:')}</strong> {t('09:00 – 17:00 (Dushanba – Juma)')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section at bottom */}
        <div className="service-reviews-section">
          <div className="reviews-header-row">
            <h3 className="reviews-section-title">{t('Xizmat sharhlari')} ({reviewsData.totalReviews})</h3>
            <button className="btn-add-review" onClick={handleOpenReviewModal}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
              <span>{t('+ Baho va sharh qoldirish')}</span>
            </button>
          </div>

          {reviewsData.reviews && reviewsData.reviews.length > 0 ? (
            <div className="reviews-list">
              {reviewsData.reviews.map((rev) => (
                <div key={rev.id} className="review-card-item">
                  <div className="reviewer-avatar" style={{ backgroundColor: rev.avatarBg || '#e0f2fe', color: rev.avatarColor || '#0284c7' }}>
                    {rev.userInitials || 'FU'}
                  </div>
                  <div className="review-content-area">
                    <div className="reviewer-top-row">
                      <span className="reviewer-name">{rev.userName}</span>
                      <span className="review-time">{t(rev.timeAgo || 'Yaqinda')}</span>
                    </div>
                    <div className="review-stars-row">
                      {Array.from({ length: rev.rating || 5 }).map((_, i) => (
                        <span key={i} className="star-gold">★</span>
                      ))}
                    </div>
                    {rev.comment && <p className="review-comment-text">{rev.comment}</p>}

                    {user && user.role === 'admin' && (
                      <div className="admin-review-card-actions">
                        <button
                          type="button"
                          className="btn-admin-rev-edit"
                          onClick={() => handleAdminEditReview(rev)}
                          title={t('Ushbu sharhni tahrirlash')}
                        >
                          ✏️ {t('Tahrirlash')}
                        </button>
                        <button
                          type="button"
                          className="btn-admin-rev-delete"
                          onClick={() => handleAdminDeleteReview(rev.id)}
                          title={t("Ushbu sharhni o'chirish")}
                        >
                          🗑️ {t("O'chirish")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="reviews-empty-state" style={{ textAlign: 'center', padding: '36px 20px', background: 'var(--card-bg, #ffffff)', borderRadius: '12px', border: '1px dashed #cbd5e1', marginTop: '16px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>💬</div>
              <p style={{ margin: '0 0 14px 0', fontSize: '15px', color: '#64748b', fontWeight: '500' }}>
                {t('Hozircha sharhlar qoldirilmagan. Ushbu xizmat boʻyicha birinchi boʻlib oʻz fikr va bahoingizni qoldiring!')}
              </p>
              <button className="btn-add-review" onClick={handleOpenReviewModal} style={{ margin: '0 auto', display: 'inline-flex' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                <span>{t('+ Fikr va baho bildirish')}</span>
              </button>
            </div>
          )}
        </div>

        {/* Modal: Baho va sharh qoldirish */}
        {showReviewModal && (
          <div className="review-modal-overlay" onClick={() => setShowReviewModal(false)}>
            <div className="review-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h4>{t('Xizmatga baho va sharh qoldirish')}</h4>
                <button className="modal-close-btn" onClick={() => setShowReviewModal(false)}>✕</button>
              </div>

              {reviewMessage && <div className="modal-alert-success">{reviewMessage}</div>}

              {!user ? (
                <div className="unauth-review-box" style={{ padding: '24px 16px', textAlign: 'center' }}>
                  <p style={{ color: '#475569', fontSize: '14px', marginBottom: '16px' }}>
                    {t('Xizmatga baho va sharh qoldirish uchun avval shaxsiy kabinetingizga kiring.')}
                  </p>
                  <button
                    type="button"
                    className="btn-submit-review"
                    onClick={() => {
                      setShowReviewModal(false);
                      navigate('/login', {
                        state: {
                          infoMessage: t('Baho va sharh qoldirish uchun avval tizimga kiring.'),
                          returnUrl: `/services/${serviceId}`
                        }
                      });
                    }}
                  >
                    {t('Tizimga kirish')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleReviewSubmit}>
                  <div className="modal-field">
                    <label>{t('Xizmatdan mamnunlik darajangiz (baho):')}</label>
                    <div className="interactive-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`star-pick ${star <= newRating ? 'selected' : ''}`}
                          onClick={() => setNewRating(star)}
                        >
                          ★
                        </span>
                      ))}
                      <span className="selected-rating-number">{newRating} / 5</span>
                    </div>
                  </div>

                  <div className="modal-field">
                    <label>{t('Izoh yoki sharhingiz:')}</label>
                    <textarea
                      rows="4"
                      placeholder={t('Xizmat tezligi, qulayligi va tizim ishlashi haqida fikringizni yozing...')}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      required
                    ></textarea>
                  </div>

                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={() => setShowReviewModal(false)}>
                      {t('Bekor qilish')}
                    </button>
                    <button type="submit" className="btn-submit-review" disabled={submittingReview}>
                      {submittingReview ? t('Yuborilmoqda...') : t('Sharhni yuborish')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Administrator koʻrish rejimi bildirishnomasi */}
        {showAdminNoticeModal && (
          <div className="review-modal-overlay" onClick={() => setShowAdminNoticeModal(false)}>
            <div className="review-modal-card admin-notice-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>🛡️</span>
                  <h3>{t('Administrator koʻrish rejimi')}</h3>
                </div>
                <button
                  type="button"
                  className="btn-close-modal"
                  onClick={() => setShowAdminNoticeModal(false)}
                >
                  ✕
                </button>
              </div>

              <div className="admin-notice-modal-body" style={{ padding: '16px 0 6px 0' }}>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                  <p style={{ margin: 0, fontSize: '14.5px', color: '#1e293b', lineHeight: '1.6' }}>
                    {lang === 'ru'
                      ? 'Вы вошли в систему как Главный администратор (Администрация портала). Администратор имеет полномочия управлять услугами (сроки, требования, условия оплаты и проверка заявлений).'
                      : lang === 'en'
                      ? 'You are logged in as Chief Administrator (Portal Administration). Administrator has permissions to manage services (deadlines, requirements, payment terms, and application review).'
                      : 'Siz tizimga Bosh Administrator (Portal maʼmuriyati) sifatida kirgansiz. Administrator xizmatlarni boshqarish (sanalar, talablar, toʻlov shartlari va arizalarni tekshirish) vakolatiga ega.'}
                  </p>
                </div>

                <p style={{ fontSize: '14.5px', color: '#475569', lineHeight: '1.6', marginBottom: '14px' }}>
                  {lang === 'ru'
                    ? 'Подача заявления через «Воспользоваться услугой» предназначена только для соискателей (докторантов и исследователей) или незарегистрированных гостей. Заявления от имени администратора не подаются.'
                    : lang === 'en'
                    ? 'Applying via "Use Service" is intended only for applicants (doctoral students and researchers) or unregistered guests. Applications cannot be submitted on behalf of the administrator.'
                    : '«Xizmatdan foydalanish» orqali ariza topshirish faqat talabgorlar (doktorant va tadqiqotchilar) yoki roʻyxatdan oʻtmagan mehmonlar uchun amal qiladi. Administrator nomidan tizimga ariza topshirilmaydi.'}
                </p>

                <p style={{ fontSize: '13.5px', color: '#64748b', lineHeight: '1.5', marginBottom: '22px' }}>
                  💡 <em>{lang === 'ru'
                    ? 'Все изменения, внесенные вами в панели администратора (сроки, таймер, предметы и требования), отображаются соискателям на этой странице в реальном времени.'
                    : lang === 'en'
                    ? 'All changes made by you in the admin panel (deadlines, timer, subjects and requirements) are displayed to applicants on this exact page in real-time.'
                    : 'Siz admin panelda kiritgan barcha oʻzgarishlaringiz (muddat, taymer, fanlar va talablar) talabgorlarga aynan shu sahifada toʻliq va real vaqtda koʻrinadi.'}</em>
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button
                    type="button"
                    className="btn-submit-review"
                    style={{ background: '#0f2b5c', padding: '12px', fontSize: '14.5px', cursor: 'pointer' }}
                    onClick={() => {
                      setShowAdminNoticeModal(false);
                      navigate('/admin');
                    }}
                  >
                    ⚙️ {t('Admin boshqaruv paneliga qaytish')}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ padding: '11px', fontSize: '13.5px', cursor: 'pointer' }}
                    onClick={handleAdminLogoutAndApply}
                  >
                    🚪 {t('Tizimdan chiqish (Talabgor sifatida ariza topshirish)')}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ padding: '8px', fontSize: '13px', background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer' }}
                    onClick={() => setShowAdminNoticeModal(false)}
                  >
                    {t('Yopish')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin Review Edit Modal */}
        {editingAdminReview && (
          <div className="review-modal-overlay" onClick={() => setEditingAdminReview(null)}>
            <div className="review-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h4>{t('Talabgor sharhini tahrirlash')}</h4>
                <button className="modal-close-btn" onClick={() => setEditingAdminReview(null)}>✕</button>
              </div>

              <form onSubmit={handleAdminSaveEdit} style={{ marginTop: '14px' }}>
                <div className="modal-field">
                  <label>{t('Talabgor F.I.Sh.:')}</label>
                  <input
                    type="text"
                    value={editAdminForm.userName}
                    onChange={(e) => setEditAdminForm({ ...editAdminForm, userName: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                    required
                  />
                </div>

                <div className="modal-field">
                  <label>{t('Baho (1 dan 5 gacha yulduz):')}</label>
                  <div className="interactive-stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={`star-pick ${star <= editAdminForm.rating ? 'selected' : ''}`}
                        onClick={() => setEditAdminForm({ ...editAdminForm, rating: star })}
                      >
                        ★
                      </span>
                    ))}
                    <span className="selected-rating-number">{editAdminForm.rating} / 5</span>
                  </div>
                </div>

                <div className="modal-field">
                  <label>{t('Sharh matni:')}</label>
                  <textarea
                    rows="3"
                    value={editAdminForm.comment}
                    onChange={(e) => setEditAdminForm({ ...editAdminForm, comment: e.target.value })}
                    required
                  ></textarea>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setEditingAdminReview(null)}>
                    {t('Bekor qilish')}
                  </button>
                  <button type="submit" className="btn-submit-review" style={{ background: '#0f2b5c' }}>
                    {t('Saqlash va reytingni yangilash')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
